/**
 * The Ward. The game's only failure state, and now its exit.
 *
 * **The punishment screen is a directory of other real people**, and that is Torn's jail page
 * deliberately rather than by accident. The first thing a newly busted player sees is that they
 * are not the only one in here, and the second is a row they could do somebody else a favour on.
 * A jail that is a countdown teaches nothing; a jail that is a list of names is the best
 * first-contact surface in the genre.
 *
 * Every price and every chance on every row was computed by the server FOR THIS VIEWER and is
 * printed before the tap. The client does no arithmetic here at all.
 *
 * Four verbs, and each costs a different thing:
 *
 *   Bail      clean cash, burned. It never reaches the inmate, which is what stops two accounts
 *             taking turns being arrested and turning the Ward into a laundering channel.
 *   Bust      four Nerve, no cash gate. The SOCIAL route is deliberately faster than the money
 *             route: Nerve regenerates and a balance does not.
 *   Self-bail dirty, at a multiple that climbs with every use inside a day.
 *   Seal      free and reversible. Heat decays at 2.0x in a cell and somebody at Heat 80 may
 *             genuinely be using the room.
 */

import { useEffect } from 'react'
import { A } from '../art'
import type { KnownAssetId } from '../art'
import type { WardRow } from '@ashport/shared'
import { Icon } from '../ui/Icon'
import { Btn, Chip, Col, Meter, Panel, PanelHead, Rail, Row, Stack } from '../ui/Kit'
import { sz, useViewport } from '../ui/viewport'
import { clock, exact, useGame } from '../game/state'
import { useSession } from '../game/session'

function Empty({ line, note }: { line: string; note: string }) {
  return (
    <Col
      gap={7}
      style={{
        flex: 1,
        minHeight: 0,
        alignItems: 'center',
        justifyContent: 'center',
        padding: '26px 18px',
        textAlign: 'center',
      }}
    >
      <Icon n="lock" s={22} style={{ opacity: 0.4 }} />
      <div className="t-head" style={{ fontSize: 12, color: 'var(--gold)', letterSpacing: '.12em' }}>{line}</div>
      <p style={{ margin: 0, maxWidth: 440, fontSize: 11.5, color: 'var(--ink-mid)', lineHeight: 1.55 }}>{note}</p>
    </Col>
  )
}

function InmateRow({ r, now }: { r: WardRow; now: number }) {
  const busy = useSession((s) => s.busy)
  const bailOut = useSession((s) => s.bailOut)
  const bustOut = useSession((s) => s.bustOut)
  return (
    <Row
      gap={9}
      style={{
        padding: '8px 9px',
        alignItems: 'center',
        background: r.me ? 'rgba(214,168,75,.08)' : 'rgba(255,255,255,.02)',
        border: `1px solid ${r.me ? 'var(--line-brass)' : 'var(--line-soft)'}`,
      }}
    >
      <img
        src={A(r.portraitId as KnownAssetId, 'ui.avatar.socket_square')}
        alt={r.displayName}
        style={{ width: 40, height: 48, flex: 'none', objectFit: 'cover', border: '1px solid var(--line)' }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <Row gap={7}>
          <span style={{ fontSize: 12, color: r.me ? 'var(--gold)' : 'var(--ink-hi)' }}>{r.handle}</span>
          <span className="t-label" style={{ fontSize: sz(8.5) }}>Level {r.level}</span>
          {r.crestId && (
            <img src={A(r.crestId as KnownAssetId)} alt={r.familyName ?? ''} style={{ width: 14, height: 14 }} />
          )}
          {r.familyName && <span className="t-label" style={{ fontSize: sz(8.5) }}>{r.familyName}</span>}
          {r.marker && <Chip c="var(--gold)">Marker</Chip>}
        </Row>
        <Row gap={7} style={{ marginTop: 4 }}>
          <Icon n="clock" s={11} c="var(--ink-dim)" />
          <span className="t-mono" style={{ fontSize: sz(10.5), color: 'var(--ink-mid)' }}>
            {clock((r.releasesAt - now) / 1000)} left
          </span>
        </Row>
      </div>

      {/*
        * BOTH PRICES ARE PRINTED, and a refusal names its own cost rather than greying out. A
        * player who cannot afford a bail should learn what it costs, not that the button is dead.
        */}
      <Col gap={4} style={{ width: 132, flex: 'none' }}>
        <Btn
          size="sm"
          full
          disabled={busy || r.bailLocked !== null}
          title={r.bailLocked ?? undefined}
          onClick={() => void bailOut(r.playerId)}
        >
          Bail {exact(r.bailPrice)}
        </Btn>
        <Btn
          v="danger"
          size="sm"
          full
          disabled={busy || r.bustLocked !== null}
          title={r.bustLocked ?? undefined}
          onClick={() => void bustOut(r.playerId)}
        >
          Bust {(r.bustBp / 100).toFixed(0)}%
        </Btn>
        {(r.bailLocked ?? r.bustLocked) !== null && (
          <span className="t-label" style={{ fontSize: sz(8), color: 'var(--ink-dim)', lineHeight: 1.3 }}>
            {r.bailLocked ?? r.bustLocked}
          </span>
        )}
      </Col>
    </Row>
  )
}

/**
 * A refusal, in voice, on the screen that caused it. Never a status code.
 *
 * The Ward had no channel for one. Four verbs live here and every one of them refuses for reasons
 * the player can act on: not enough clean for a bail, not enough Nerve for a bust, an escalating
 * self-bail multiple they cannot cover, somebody else got the door open first. All four were
 * silent, and a bust that fails is indistinguishable on screen from a button that is broken.
 */
function Refusal() {
  const text = useSession((s) => s.refusal)
  const clear = useSession((s) => s.clearRefusal)
  if (text === null) return null
  return (
    <Panel tone="danger" style={{ flex: 'none' }}>
      <PanelHead
        title="No"
        icon="warn"
        accent="var(--arrest)"
        right={
          <Btn v="ghost" size="sm" onClick={clear}>
            Close
          </Btn>
        }
      />
      <p style={{ margin: 0, padding: '9px 12px 12px', fontSize: 12, color: 'var(--ink)' }}>{text}</p>
    </Panel>
  )
}

/** The card that follows a bail or a bust, including the one button that starts a sponsorship. */
function Outcome() {
  const out = useSession((s) => s.lastWard)
  const clear = useSession((s) => s.clearWard)
  const offerVouch = useSession((s) => s.offerVouch)
  const busy = useSession((s) => s.busy)
  if (!out) return null
  return (
    <Panel tone={out.landed ? 'gold' : 'danger'} style={{ flex: 'none' }}>
      <PanelHead
        title={out.headline}
        icon={out.landed ? 'check' : 'warn'}
        accent={out.landed ? 'var(--gold)' : 'var(--arrest)'}
        right={
          <Btn v="ghost" size="sm" onClick={clear}>
            Close
          </Btn>
        }
      />
      <div style={{ padding: '9px 12px 12px' }}>
        <p style={{ margin: 0, fontSize: 11.5, color: 'var(--ink-mid)', lineHeight: 1.55 }}>{out.body}</p>
        {out.markerOpened && (
          <p style={{ margin: '8px 0 0', fontSize: 11.5, color: 'var(--gold)', lineHeight: 1.5 }}>
            They owe you a Marker for the next 72 hours. If you end up in a cell while it is live,
            their bail price for you halves and their odds against your door improve.
          </p>
        )}
        {/*
          * THE SPONSORSHIP SEED. GAME-DESIGN:315 says bail exists because "a Boss bailing out a
          * stranger's Associate is exactly the moment a sponsorship starts". This is that moment,
          * with the button on it, and the offer stays anonymous so the rescue does not become a
          * popularity transaction.
          */}
        {out.canVouch && out.vouchPlayerId && (
          <Btn
            v="gold"
            size="sm"
            full
            style={{ marginTop: 10 }}
            disabled={busy}
            onClick={() => void offerVouch(out.vouchPlayerId as string)}
          >
            Vouch for this player
          </Btn>
        )}
      </div>
    </Panel>
  )
}

/** Your own cell: the countdown, the Seal switch and the expensive way out. */
function MyCell() {
  const { now } = useGame()
  const w = useSession((s) => s.ward)
  const busy = useSession((s) => s.busy)
  const selfBail = useSession((s) => s.selfBail)
  const sealCell = useSession((s) => s.sealCell)
  if (!w) return null

  if (!w.self.inJail) {
    return (
      <Panel style={{ flex: 'none' }}>
        <PanelHead title="Your Cell" icon="lock" />
        <div style={{ padding: '10px 12px 12px' }}>
          <p style={{ margin: 0, fontSize: 11.5, color: 'var(--ink-mid)', lineHeight: 1.55 }}>
            You are not in one. When you are, this is where the clock runs, and everybody on the
            list above can see you the whole time.
          </p>
        </div>
      </Panel>
    )
  }

  return (
    <Panel tone="danger" style={{ flex: 'none' }}>
      <PanelHead title="Your Cell" icon="lock" accent="var(--arrest)" />
      <div style={{ padding: '10px 12px 12px' }}>
        <div className="t-display" style={{ fontSize: 30, color: 'var(--arrest)', lineHeight: 1 }}>
          {clock((w.self.releasesAt - now) / 1000)}
        </div>
        <div style={{ marginTop: 8 }}>
          <Meter v={1} max={1} a="var(--brass-deep)" b="var(--arrest)" h={4} />
        </div>
        <p style={{ margin: '9px 0 0', fontSize: 11.5, color: 'var(--ink-mid)', lineHeight: 1.5 }}>
          Heat decays twice as fast in here, and however you leave you get ten minutes nobody can
          touch you. That is true even if you sit the whole thing out.
        </p>

        <Btn
          v="danger"
          size="sm"
          full
          style={{ marginTop: 10 }}
          disabled={busy || w.self.selfBailLocked !== null}
          title={w.self.selfBailLocked ?? undefined}
          onClick={() => void selfBail()}
        >
          Buy your way out, {exact(w.self.selfBailPrice)} dirty
        </Btn>
        <p style={{ margin: '6px 0 0', fontSize: sz(10.5), color: 'var(--ink-dim)', lineHeight: 1.45 }}>
          {w.self.selfBailsToday > 0
            ? `That is your ${w.self.selfBailsToday + 1} today, and the price knows it.`
            : 'Somebody else getting you out is always cheaper. That is the point.'}
        </p>

        <Btn
          v="ghost"
          size="sm"
          full
          style={{ marginTop: 9 }}
          disabled={busy}
          onClick={() => void sealCell(!w.self.sealed)}
        >
          {w.self.sealed ? 'Unseal the cell' : 'Seal the cell'}
        </Btn>
        <p style={{ margin: '6px 0 0', fontSize: sz(10.5), color: 'var(--ink-dim)', lineHeight: 1.45 }}>
          Sealing takes you off the list and refuses every bail and every bust. Nobody needs it to
          be safe, because leaving early is never worse than waiting. It is here for the player who
          is using the room.
        </p>
      </div>
    </Panel>
  )
}

/** The weekly Blotter, and the one line that decides what it is worth. */
function Blotter() {
  const w = useSession((s) => s.ward)
  if (!w) return null
  return (
    <Panel style={{ flex: 1, minHeight: 0 }}>
      <PanelHead
        title="The Blotter"
        icon="trophy"
        sub="This week, by people helped"
        right={
          <span className="t-mono" style={{ fontSize: sz(10.5), color: 'var(--gold)' }}>
            you {w.peopleHelped}
          </span>
        }
      />
      <div className="scroll-y" style={{ flex: 1, padding: '6px 10px 10px' }}>
        <p style={{ margin: '0 0 8px', fontSize: sz(10.5), color: 'var(--ink-dim)', lineHeight: 1.45 }}>
          Ranked by how many DIFFERENT people you got out, never by how many times and never by
          what you spent. Helping the same friend forty times counts once.
        </p>
        {w.blotter.length === 0 ? (
          <p style={{ margin: 0, fontSize: 11.5, color: 'var(--ink-mid)' }}>
            Nobody has got anybody out this week.
          </p>
        ) : (
          w.blotter.map((b) => (
            <Row
              key={b.n}
              gap={8}
              style={{ padding: '5px 3px', borderBottom: '1px solid var(--line-soft)' }}
            >
              <span className="t-num" style={{ width: 18, color: b.n <= 3 ? 'var(--gold)' : 'var(--ink-dim)' }}>
                {b.n}
              </span>
              <span style={{ flex: 1, fontSize: 11.5, color: b.me ? 'var(--gold)' : 'var(--ink)' }}>
                {b.handle}
              </span>
              <span className="t-num" style={{ fontSize: 12, color: 'var(--ink-hi)' }}>{b.peopleHelped}</span>
            </Row>
          ))
        )}
      </div>
    </Panel>
  )
}

/** Every live obligation you are carrying, and who it is owed to. */
function Markers() {
  const { now } = useGame()
  const w = useSession((s) => s.ward)
  if (!w || w.markers.length === 0) return null
  return (
    <Panel tone="gold" style={{ flex: 'none' }}>
      <PanelHead title="Markers" icon="rank" accent="var(--gold)" sub="What you owe" />
      <div style={{ padding: '8px 12px 11px' }}>
        {w.markers.map((m) => (
          <Row key={m.id} gap={8} style={{ padding: '4px 0' }}>
            <span style={{ flex: 1, fontSize: 11.5, color: 'var(--ink)' }}>{m.owedToHandle}</span>
            <span className="t-mono" style={{ fontSize: sz(10.5), color: 'var(--gold)' }}>
              {clock((m.expiresAt - now) / 1000)}
            </span>
          </Row>
        ))}
        <p style={{ margin: '7px 0 0', fontSize: sz(10.5), color: 'var(--ink-dim)', lineHeight: 1.45 }}>
          Single use. If one of them ends up in a cell while this is live, their bail costs you
          half and your odds against their door go up ten points.
        </p>
      </div>
    </Panel>
  )
}

/* ------------------------------------------------------------------ screen */

export function Ward() {
  const { now } = useGame()
  const w = useSession((s) => s.ward)
  const load = useSession((s) => s.loadWard)

  useEffect(() => {
    void load().catch(() => undefined)
  }, [load])
  const { stacked } = useViewport()

  return (
    <Stack>
      <Col gap={8} style={stacked ? { flex: 'none', minWidth: 0 } : { flex: 1, minWidth: 0 }}>
        <Panel style={{ flex: 'none' }}>
          <Row gap={14} style={{ padding: '7px 12px 9px', flexWrap: 'wrap' }}>
            <Icon n="lock" s={26} c="var(--brass-lit)" />
            <div style={{ flex: 'none' }}>
              <div className="t-display" style={{ fontSize: 21, color: 'var(--ink-hi)' }}>The Ward</div>
              <div className="t-label" style={{ fontSize: sz(8.5), marginTop: 2 }}>
                Everybody the city is holding right now
              </div>
            </div>
            <div style={{ flex: 1 }} />
            <span className="t-mono" style={{ fontSize: 11, color: 'var(--ink-dim)' }}>
              {w?.rows.length ?? 0} inside
            </span>
          </Row>
        </Panel>

        <Refusal />
        <Outcome />

        <Panel style={{ flex: 1, minHeight: 0 }}>
          <PanelHead title="Inside" icon="lock" sub="Longest held first, because that is the biggest favour" />
          {!w ? (
            <Empty line="Reading the roll" note="One moment." />
          ) : w.rows.length === 0 ? (
            <Empty
              line="Nobody is in a cell"
              note="The city is quiet. When somebody gets picked up they appear here with the time they have left, what their bail costs you and what your odds are on their door, and any of it can be paid by a person who has never met them."
            />
          ) : (
            <div className="scroll-y" style={{ flex: 1, padding: '8px 10px 10px' }}>
              <Col gap={6}>
                {w.rows.map((r) => (
                  <InmateRow key={r.playerId} r={r} now={now} />
                ))}
              </Col>
            </div>
          )}
        </Panel>
      </Col>

      <Rail width={300}>
        <MyCell />
        <Markers />
        <Blotter />
      </Rail>
    </Stack>
  )
}
